/**
 * Landing contact form — redesign pass A.
 *
 * Replaces the old mailto: + browser-alert handler with a real POST through
 * the `/api/send` pipeline (`sendContactMessage` in lib/sendApi.ts):
 *
 *   - inline per-field validation (no native bubbles, no alert())
 *   - an on-brand in-page success state once the POST lands
 *   - an on-brand inline error state when the endpoint is unreachable,
 *     with a direct-email fallback so the visitor is never stranded
 *   - a working privacy-statement link (modal, placeholder copy)
 */

import React, { useState } from 'react';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { sendContactMessage } from '../lib/sendApi';
import { PrivacyStatementModal } from './modals/PrivacyStatementModal';

interface Props {
  /** Brand inbox shown as the fallback in the error state. */
  contactEmail: string;
  /** `modals.contactFormThanks` from the content surface, when present. */
  thanks?: { title: string; body: string } | undefined;
}

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface FieldErrors {
  name?: string;
  email?: string;
  message?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(name: string, email: string, message: string): FieldErrors {
  const errors: FieldErrors = {};
  if (!name.trim()) errors.name = 'Please tell us your name.';
  if (!email.trim()) {
    errors.email = 'Please enter your email address.';
  } else if (!EMAIL_RE.test(email.trim())) {
    errors.email = 'That doesn’t look like an email address.';
  }
  if (!message.trim()) errors.message = 'Please write a short message.';
  return errors;
}

const inputClass = (invalid: boolean) =>
  `w-full bg-white/10 border rounded-xl px-6 py-4 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all ${
    invalid ? 'border-white/80' : 'border-white/20'
  }`;

function FieldError({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <p
      id={id}
      role="alert"
      className="flex items-center gap-1.5 text-sm font-century font-bold text-white bg-black/20 rounded-lg px-3 py-1.5 w-fit"
    >
      <AlertCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}

export function ContactForm({ contactEmail, thanks }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fieldErrors = validate(name, email, message);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setStatus('submitting');
    try {
      await sendContactMessage({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div
        data-testid="contact-success"
        className="flex flex-col items-center text-center py-10 space-y-6"
      >
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
          <Check className="w-10 h-10 text-[#356d4e]" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h4 className="text-3xl font-bold font-century">
            {thanks?.title ?? 'Thank you!'}
          </h4>
          <p className="text-lg opacity-90 font-century">
            {thanks?.body ?? 'Your message has been sent.'} We typically reply
            within one working day.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setName('');
            setEmail('');
            setMessage('');
            setErrors({});
            setStatus('idle');
          }}
          className="font-century text-sm underline underline-offset-4 opacity-80 hover:opacity-100 transition-opacity"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <>
      <form className="space-y-8" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <div className="space-y-2">
          <label htmlFor="name" className="block text-lg font-century font-medium opacity-90">
            Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            autoComplete="name"
            maxLength={200}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'name-error' : undefined}
            className={inputClass(Boolean(errors.name))}
          />
          {errors.name && <FieldError id="name-error">{errors.name}</FieldError>}
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="block text-lg font-century font-medium opacity-90">
            Email address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            autoComplete="email"
            maxLength={254}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'email-error' : undefined}
            className={inputClass(Boolean(errors.email))}
          />
          {errors.email && <FieldError id="email-error">{errors.email}</FieldError>}
        </div>

        <div className="space-y-2">
          <label htmlFor="message" className="block text-lg font-century font-medium opacity-90">
            Leave a message
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={5000}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
            }}
            aria-invalid={Boolean(errors.message)}
            aria-describedby={errors.message ? 'message-error' : undefined}
            className={`${inputClass(Boolean(errors.message))} resize-none`}
          />
          {errors.message && <FieldError id="message-error">{errors.message}</FieldError>}
        </div>

        {status === 'error' && (
          <div
            data-testid="contact-error"
            role="alert"
            className="bg-white rounded-2xl p-5 flex items-start gap-3 text-[#444444] shadow-lg"
          >
            <AlertCircle className="w-5 h-5 text-[#356d4e] shrink-0 mt-0.5" aria-hidden="true" />
            <div className="text-sm leading-relaxed font-century">
              <p className="font-bold mb-1">We couldn&rsquo;t send your message right now.</p>
              <p>
                Please try again in a moment, or email us directly at{' '}
                <a
                  href={`mailto:${contactEmail}`}
                  className="font-bold text-[#356d4e] underline underline-offset-2"
                >
                  {contactEmail}
                </a>
                .
              </p>
            </div>
          </div>
        )}

        <div className="pt-4 space-y-4">
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full bg-gray-50 text-[#444444] font-bold py-5 rounded-full text-xl tracking-widest hover:bg-white transition-colors shadow-lg uppercase disabled:opacity-60 flex items-center justify-center gap-3"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                Sending&hellip;
              </>
            ) : (
              'Send message'
            )}
          </button>
          <p className="text-center text-xs opacity-80 font-century tracking-wide">
            By sending this form you agree to our{' '}
            <button
              type="button"
              onClick={() => setPrivacyOpen(true)}
              className="underline underline-offset-2 font-bold hover:opacity-100"
            >
              privacy statement
            </button>
            .
          </p>
        </div>
      </form>
      <PrivacyStatementModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </>
  );
}
